import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { BehaviorSubject } from 'rxjs';
import { CountryCode } from 'libphonenumber-js';
import { DetailPair } from '../../../../../shared/components/detail-info-card/DetailPair';
import { OrgBasicInfoDialogComponent } from './org-basic-info-dialog/org-basic-info-dialog.component';
import { Logger, LogLevel } from '../../../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../../../infrastructure/context/user-context-provider';
import { dateFormat } from '../../../../../utilities/DateUtility';
import { formatPhoneNumber } from '../../../../../utilities/PhoneUtility';
import { DetailInfoCardComponent } from '../../../../../shared/components/detail-info-card/detail-info-card.component';
import { SearchService } from '../../../../../shared/search/search.service';
import { PropertyApiService } from '../../../../properties/services/property.api.service';

@Component({
  selector: 'app-org-basic-info',
  templateUrl: './org-basic-info.component.html',
  styleUrls: ['./org-basic-info.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DetailInfoCardComponent,
    MatProgressSpinner
  ]
})
export class OrgBasicInfoComponent implements OnInit {

  @Input() org: any;
  @Input() embeddedOrganizationBoolean: any;
  @Output() saveEvent = new EventEmitter<void>();
  @Output() organizationUpdatedEvent = new EventEmitter<any>();

  loading = new BehaviorSubject<boolean>(false);
  basicInfo: DetailPair[] = [];
  shouldDisplayLogo: boolean = true;

  constructor(
    public dialog: MatDialog, 
    private userContextProvider: UserContextProvider,
    private searchService: SearchService,
    private propertyService: PropertyApiService,
    private logService: Logger
  ) { }

  ngOnInit(): void {
    if (this.embeddedOrganizationBoolean) {
      this.shouldDisplayLogo = false;
      this.populateEmbeddedOrgBasicInfo(this.org);
    } else {
      this.populateBasicInfo(this.org);
    }
  }

  resetState() {
    this.shouldDisplayLogo = true;
  }

  async populateBasicInfo(organization: any) {
    try {
      await this.configureLogo(organization);

      let safely_service = '';
      if (organization.for_reservation && organization.for_guest_verification) {
        safely_service = 'Insurance Protection and Guest Verification';
      }
      if (organization.for_reservation && !organization.for_guest_verification) {
        safely_service = 'Insurance Protection Only';
      }
      if (!organization.for_reservation && organization.for_guest_verification) {
        safely_service = 'Guest Verification Only';
      }

      this.basicInfo = [];
      this.basicInfo.push(new DetailPair("Organization ID", `${organization.id}`));
      this.basicInfo.push(new DetailPair("Guest Friendly Name", `${organization.org_name_to_guest}`));
      this.basicInfo.push(new DetailPair("Contact Name", `${organization.contact_first_name} ${organization.contact_last_name}`));
      this.basicInfo.push(new DetailPair("Phone Number", formatPhoneNumber(organization.phone_number, this.userContextProvider.countryCode as CountryCode)));
      this.basicInfo.push(new DetailPair("Website", organization.website));
      this.basicInfo.push(new DetailPair("Email Address", organization.email_address));
      this.basicInfo.push(new DetailPair("Notification Email Address", organization.notification_email));
      this.basicInfo.push(new DetailPair("Safely Service", safely_service));
      this.basicInfo.push(new DetailPair("Status", organization.status.display_text));
      this.basicInfo.push(new DetailPair("Claim Payments via ACH", organization.send_check_to_safely === 0 ? "No" : "Yes"));
      this.basicInfo.push(new DetailPair("Automated Guest Screening Emails", organization.email_guest_for_details === 0 ? "No" : "Yes", 'Automated Screening Emails are automatically generated to the guest when a reservation with an email address populates into your Safely portal account.'));
      this.basicInfo.push(new DetailPair("Email When Guest Verification Complete", organization.notify_guest_verification === 0 ? "No" : "Yes"));
      this.basicInfo.push(new DetailPair("API Access Key", organization.access_key));
      this.basicInfo.push(new DetailPair("Created At", dateFormat(organization.created_date, this.userContextProvider.region)));

      // TODO: Need value for Embedded Account Type:
      // if (organization.my_vr_code != null) {
      //   this.basicInfo.push(new DetailPair("Account Type", organization.my_vr_code));
      // }
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Organization information', 'Error while retrieving Organization information');
    }
  }

  async populateEmbeddedOrgBasicInfo(partnerOrganization: any) {
    try {
      this.loading.next(true);

      let allEmbeddedOrgPropertiesLength: number = 0;
      let activeEmbeddedOrgPropertiesLength: number = 0;
      let inactiveEmbeddedOrgPropertiesLength: number = 0;

      if (partnerOrganization.organizationId && partnerOrganization.pillStatus != 'ONBOARDING_IN_PROGRESS') {
        try {
          this.searchService.setOrganizationId(Number(partnerOrganization.organizationId));
          const { properties, totalRecords} = await this.propertyService.searchProperties(this.searchService.currentQuery);
          if (properties) {
            allEmbeddedOrgPropertiesLength = totalRecords;
          } 
        } catch(e) {
          this.logService.log({ message: `An error while retrieving Total Properties count for Partner Organization ID: ${ partnerOrganization.organizationId }`, level: LogLevel.DEBUG, error: `Error while retrieving Total Properties count for Partner Organization ID: ${ partnerOrganization.organizationId }` });
        }

        try {
          let partnerOrgPropertyPolicyCount = await this.propertyService.getPartnerOrgPropertyPolicyCountByOrganizationId(partnerOrganization.organizationId);
          activeEmbeddedOrgPropertiesLength = partnerOrgPropertyPolicyCount.activePropertyPolicyCount;
          inactiveEmbeddedOrgPropertiesLength = partnerOrgPropertyPolicyCount.inactivePropertyPolicyCount;
        } catch(e) {
          this.logService.log({ message: `An error while retrieving Active/Inactive Property Policies counts for Partner Organization ID: ${ partnerOrganization.organizationId }: ${e}`, level: LogLevel.DEBUG, error: `Error while retrieving Active/Inactive Property Policies counts for Partner Organization ID: ${ partnerOrganization.organizationId }: ${e}` });
        }
      }

      let activation_date = '';
      let deactivation_date = '';
      if (partnerOrganization.pillStatus === 'ACTIVE' || partnerOrganization.pillStatus === 'PENDING_ACTIVE') {
        activation_date = partnerOrganization.coverageStartDate;
      }
      if (partnerOrganization.pillStatus === 'INACTIVE' || partnerOrganization.pillStatus === 'PENDING_INACTIVE' || partnerOrganization.pillStatus === 'TERMINATED') {
        deactivation_date = partnerOrganization.coverageEndDate;
      }

      this.basicInfo = [];
      this.basicInfo.push(new DetailPair("Organization ID", `${partnerOrganization.organizationId}`));
      this.basicInfo.push(new DetailPair("Reference ID", `${partnerOrganization.referenceId}`));
      this.basicInfo.push(new DetailPair("Guest Friendly Name", `${partnerOrganization.name}`));
      this.basicInfo.push(new DetailPair("Partner ID", `${partnerOrganization.partnerId}`));
      this.basicInfo.push(new DetailPair("Website", partnerOrganization.website));
      this.basicInfo.push(new DetailPair("", ""));
      this.basicInfo.push(new DetailPair("Contact Name", `${partnerOrganization.contactName}`));
      this.basicInfo.push(new DetailPair("Phone Number", formatPhoneNumber(partnerOrganization.phone, this.userContextProvider.countryCode as CountryCode)));
      this.basicInfo.push(new DetailPair("Email", partnerOrganization.email));
      this.basicInfo.push(new DetailPair("Total Properties", allEmbeddedOrgPropertiesLength));
      this.basicInfo.push(new DetailPair("Active Properties", activeEmbeddedOrgPropertiesLength));
      this.basicInfo.push(new DetailPair("Inactive Properties", inactiveEmbeddedOrgPropertiesLength));

      const formattedStatus = partnerOrganization.pillStatus.toLowerCase().split('_')
        .map((statusString: string) => statusString.charAt(0).toUpperCase() + statusString.slice(1))
        .join(' ');
      this.basicInfo.push(new DetailPair("Status", formattedStatus));

      if (activation_date) {
        this.basicInfo.push(new DetailPair("Activation Date", `${dateFormat(activation_date, this.userContextProvider.region)}`));
      }

      if (deactivation_date) {
        this.basicInfo.push(new DetailPair("Deactivation Date", `${dateFormat(deactivation_date, this.userContextProvider.region)}`));
      }

      this.basicInfo.push(new DetailPair("Status Reason", partnerOrganization.statusReason));
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Partner Organization information', 'Error while retrieving Partner Organization information');
    } finally {
      this.loading.next(false);
    }
  }

  async configureLogo(organization: any) {
    let logo = organization.logo || {};
    if (organization.photo) {
      logo.photo_url = organization.photo;
    }
    if (organization.photo_id) {
      logo.photo_id = organization.photo_id;
    }
    organization.logo = logo;
  }

  couldNotLoadLogo() {
    this.shouldDisplayLogo = false;
  }

  onEditBasicInfoClick() {
    const dialogRef = this.dialog.open(OrgBasicInfoDialogComponent, {
      width: '60%',
      disableClose: true,
      data: {
        org: this.org
      }
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.populateBasicInfo(data);
        this.organizationUpdatedEvent.emit(data);
      }
    });
  }
}